import React from 'react';
import moment from 'moment';
import { Checkbox } from '@getsynapse/design-system';
import { PROJECT_STATUS, NEW_PROJECT_FORM_FIELDS } from 'utils/constants';
import {
  FormOption,
  rangeDateType,
  objKeyAsString,
  NewProject,
} from 'utils/customTypes';
import { ProjectTemplateField } from 'utils/types/templates';

export const MultipleOptionLI: React.FC<{
  label: string;
  isSelected: boolean;
  selectOption: (option: any) => void;
  disabled?: boolean;
}> = ({ label, isSelected = false, selectOption, disabled = false }) => (
  <Checkbox
    checked={isSelected}
    disabled={disabled}
    onChange={selectOption}
    label={label}
    inputProps={{
      className: `${
        isSelected &&
        'group-hover:border-neutral-white group-focus-visible:border-neutral-white'
      }`,
    }}
    className={`${
      isSelected &&
      'group-hover:text-neutral-white group-focus-visible:text-neutral-white'
    }`}
  />
);

export const getNewProjectStageValue = (
  newProjectStatus: string,
  baseProjectStageId: string | undefined,
  stageOptions: FormOption[]
) => {
  let newStageValue: string | null = baseProjectStageId || null;
  const noneStageProjectStatusArray = [
    PROJECT_STATUS.NEW,
    PROJECT_STATUS.IN_PLANNING,
  ];
  if (newProjectStatus === PROJECT_STATUS.IN_PROGRESS && !baseProjectStageId) {
    newStageValue = stageOptions[0].value;
  }
  if (noneStageProjectStatusArray.includes(newProjectStatus)) {
    newStageValue = null;
  }
  const updatedData = {
    [NEW_PROJECT_FORM_FIELDS.STATUS]: newProjectStatus,
    [NEW_PROJECT_FORM_FIELDS.STAGE]: newStageValue,
  };
  return { updatedData };
};

export const resetTargetLauchDateWhenGreaterStartDateIsSelected = (
  dateRange: rangeDateType,
  launchDate: string
) => {
  const { startDate, endDate } = dateRange;
  // startDate is a raw local-midnight picker Date; launchDate is a UTC-noon
  // ISO stamp. Compare CALENDAR DAYS, not instants: the picker Date's day
  // is its local parts (the day the user saw), the stamp's day is its
  // literal ISO date part, and ISO day strings order lexicographically.
  // Instant comparison (moment(...).isAfter) silently returned false at
  // UTC+12..+14 for a same-day-difference pair (local midnight of day N+1
  // IS N T12:00Z), so the guard missed the "start moved past launch" case
  // and saved an internally inconsistent project. Day-string comparison is
  // also independent of fromUtcNoonIso, whose constructor-based inverse
  // reads the real process TZ and can't be exercised by the getter-spy
  // tests covering this guard.
  const startDay = moment(startDate).format('YYYY-MM-DD');
  const launchDay = String(launchDate).slice(0, 10);
  const shouldResetLaunchDate = startDay > launchDay;
  const updatedDates: objKeyAsString = {
    [NEW_PROJECT_FORM_FIELDS.START_DATE]: startDate,
    [NEW_PROJECT_FORM_FIELDS.TARGET_COMPLETION_DATE]: endDate,
    [NEW_PROJECT_FORM_FIELDS.TARGET_LAUNCH_DATE]: launchDate,
  };
  if (shouldResetLaunchDate) {
    updatedDates[NEW_PROJECT_FORM_FIELDS.TARGET_LAUNCH_DATE] = undefined;
  }
  return { shouldResetLaunchDate, updatedDates };
};

export const formatNewProjectDateTypeFields = (
  projectData: NewProject,
  projectTemplateFields: ProjectTemplateField[]
) => {
  const formattedProjectData = { ...projectData };
  const dateTypeTemplateFields = projectTemplateFields.filter(
    (field) => field.field_template.type === 'date'
  );
  if (projectData.startDate) {
    formattedProjectData.startDate = new Date(
      projectData.startDate
    ).toLocaleDateString('en-US');
  }
  if (projectData.targetCompletionDate) {
    formattedProjectData.targetCompletionDate = new Date(
      projectData.targetCompletionDate
    ).toLocaleDateString('en-US');
  }
  dateTypeTemplateFields.forEach((field) => {
    if (projectData[field.field_template.name]) {
      formattedProjectData[field.field_template.name] = {
        ...projectData[field.field_template.name],
        val: new Date(
          projectData[field.field_template.name].val
        ).toLocaleDateString('en-US'),
      };
    }
  });
  return formattedProjectData;
};
